import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import useMultiplayer from './useMultiplayer';
import { storageKey } from './storageKeys';
import RoomPanel from './RoomPanel';
import GameShell from './game/GameShell';
import usePwaInstall from './usePwaInstall';
import ScoreEntry, { displayRoundScore } from './ScoreEntry';

/* ==============================
   PRESET AVATARS (inline SVGs)
   ============================== */
const PRESET_AVATARS = [
  {
    id: 'crown', label: 'Crown',
    render: (sz) => (
      <svg viewBox="0 0 24 24" width={sz} height={sz} fill="#c9a84c">
        <path d="M2 20h20v2H2zM5 17h14l2-10-5 4-4-6-4 6-5-4z"/>
      </svg>
    ),
  },
  {
    id: 'spade', label: 'Spade',
    render: (sz) => (
      <svg viewBox="0 0 24 24" width={sz} height={sz} fill="#e2d9c8">
        <path d="M12 2C8 7 3 10 3 14a5 5 0 004.5 5c1.3 0 2.5-.5 3.5-1.5v3.5h2v-3.5c1 1 2.2 1.5 3.5 1.5A5 5 0 0021 14c0-4-5-7-9-12z"/>
      </svg>
    ),
  },
  {
    id: 'heart', label: 'Heart',
    render: (sz) => (
      <svg viewBox="0 0 24 24" width={sz} height={sz} fill="#f87171">
        <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
      </svg>
    ),
  },
  {
    id: 'diamond', label: 'Diamond',
    render: (sz) => (
      <svg viewBox="0 0 24 24" width={sz} height={sz} fill="#60a5fa">
        <path d="M12 2L2 12l10 10 10-10z"/>
      </svg>
    ),
  },
  {
    id: 'club', label: 'Club',
    render: (sz) => (
      <svg viewBox="0 0 24 24" width={sz} height={sz} fill="#4ade80">
        <path d="M12 2a5 5 0 00-2.5 9.33A5 5 0 005 16a5 5 0 005 0h-1v4h6v-4h-1a5 5 0 005 0 5 5 0 00-4.5-4.67A5 5 0 0012 2z"/>
      </svg>
    ),
  },
  {
    id: 'chip', label: 'Poker Chip',
    render: (sz) => (
      <svg viewBox="0 0 24 24" width={sz} height={sz}>
        <circle cx="12" cy="12" r="10" fill="#166534" stroke="#c9a84c" strokeWidth="1.5"/>
        <circle cx="12" cy="12" r="7" fill="none" stroke="#c9a84c" strokeWidth="0.8" strokeDasharray="3 2"/>
        <circle cx="12" cy="12" r="4" fill="#c9a84c" opacity="0.3"/>
        <text x="12" y="15" textAnchor="middle" fontSize="8" fill="#c9a84c" fontWeight="bold">$</text>
      </svg>
    ),
  },
  {
    id: 'dragon', label: 'Dragon',
    render: (sz) => (
      <svg viewBox="0 0 24 24" width={sz} height={sz} fill="#f59e0b">
        <path d="M7 4l1 2-3 3 4 1 1 4 3-3 3 3 1-4 4-1-3-3 1-2-4 2-3-4-3 4z"/>
        <path d="M12 14l-2 6h4z" opacity="0.7"/>
      </svg>
    ),
  },
  {
    id: 'skull', label: 'Skull',
    render: (sz) => (
      <svg viewBox="0 0 24 24" width={sz} height={sz} fill="#e2d9c8">
        <path d="M12 2C7 2 3 6 3 11c0 3 1.5 5.5 4 7v3h2v-1h2v1h2v-1h2v1h2v-3c2.5-1.5 4-4 4-7 0-5-4-9-9-9zM9 13a2 2 0 110-4 2 2 0 010 4zm6 0a2 2 0 110-4 2 2 0 010 4z"/>
      </svg>
    ),
  },
  {
    id: 'star', label: 'Star',
    render: (sz) => (
      <svg viewBox="0 0 24 24" width={sz} height={sz} fill="#fbbf24">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.27 5.82 22 7 14.14 2 9.27l6.91-1.01z"/>
      </svg>
    ),
  },
  {
    id: 'flame', label: 'Flame',
    render: (sz) => (
      <svg viewBox="0 0 24 24" width={sz} height={sz} fill="#f97316">
        <path d="M12 23c-4.97 0-8-3.58-8-7.5 0-3.07 2-5.84 4-7.5-.22 2.5 1.27 4 3 5.5.5-3 2.5-6.5 5-10 0 5 4 7.5 4 12C20 19.42 16.97 23 12 23z"/>
        <path d="M12 23c-2.21 0-4-2.24-4-5 0-2 1-3 2-4 .5 1.5 1.5 2 2 2s1.5-.5 2-2c1 1 2 2 2 4 0 2.76-1.79 5-4 5z" fill="#fbbf24"/>
      </svg>
    ),
  },
  {
    id: 'lightning', label: 'Lightning',
    render: (sz) => (
      <svg viewBox="0 0 24 24" width={sz} height={sz} fill="#facc15">
        <path d="M13 2L4 14h7l-2 8 11-12h-7z"/>
      </svg>
    ),
  },
  {
    id: 'wolf', label: 'Wolf',
    render: (sz) => (
      <svg viewBox="0 0 24 24" width={sz} height={sz} fill="#a1a1aa">
        <path d="M4 4l3 8-3 4h4l4 6 4-6h4l-3-4 3-8-6 3-2-5-2 5z"/>
      </svg>
    ),
  },
];

/* ==============================
   POKER TABLE SVG COMPONENT
   ============================== */
const TABLE_W = 700;
const TABLE_H = 438;
const TABLE_FELT_INSET = 30;
const SEAT_ANCHOR_ANGLES = [
  Math.PI / 2,
  (3 * Math.PI) / 4,
  Math.PI,
  (-3 * Math.PI) / 4,
  -Math.PI / 2,
  -Math.PI / 4,
  0,
  Math.PI / 4,
];
const SEAT_INDEX_MAP = {
  1: [0],
  2: [0, 4],
  3: [0, 3, 5],
  // Four and six players use diagonal/side anchors so larger adaptive seats
  // leave the central Submit control unobstructed.
  4: [1, 3, 5, 7],
  5: [0, 2, 3, 5, 6],
  6: [1, 2, 3, 5, 6, 7],
  7: [0, 1, 2, 3, 5, 6, 7],
  8: [0, 1, 2, 3, 4, 5, 6, 7],
};

function getSeatEllipseRadii(isMobile, playerCount = 0) {
  const cx = TABLE_W / 2;
  const cy = TABLE_H / 2;
  const feltRx = cx - TABLE_FELT_INSET;
  const feltRy = cy - TABLE_FELT_INSET;

  return {
    feltRx,
    feltRy,
    seatRx: feltRx + (isMobile ? -20 : 10),
    // Seven/eight mobile seats need more vertical separation: each seat also
    // carries a name row and score controls beneath its avatar. Keeping the
    // compact radius used by smaller groups lets those controls collide with
    // the next diagonal avatar.
    seatRy: feltRy + (isMobile ? (playerCount >= 7 ? 16 : -10) : 10),
  };
}

function PokerTableSVG({ width, height }) {
  const cx = width / 2;
  const cy = height / 2;
  const rx = cx - TABLE_FELT_INSET;
  const ry = cy - TABLE_FELT_INSET;
  const fontSize = Math.round(rx * 0.22);

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      height="100%"
      style={{ position: 'absolute', inset: 0 }}
    >
      <defs>
        <radialGradient id="feltGrad" cx="50%" cy="50%" r="60%">
          <stop offset="0%" stopColor="#1a4d2e" />
          <stop offset="70%" stopColor="#0f3321" />
          <stop offset="100%" stopColor="#0a2418" />
        </radialGradient>
        <radialGradient id="glowGrad" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(201,168,76,0.06)" />
          <stop offset="100%" stopColor="transparent" />
        </radialGradient>
      </defs>

      <ellipse cx={cx} cy={cy} rx={rx + 14} ry={ry + 14}
        fill="none" stroke="#3d2b1a" strokeWidth="18" />
      <ellipse cx={cx} cy={cy} rx={rx + 6} ry={ry + 6}
        fill="none" stroke="#c9a84c" strokeWidth="1.5" opacity="0.6" />
      <ellipse cx={cx} cy={cy} rx={rx} ry={ry}
        fill="url(#feltGrad)" />
      <ellipse cx={cx} cy={cy} rx={rx * 0.7} ry={ry * 0.7}
        fill="url(#glowGrad)" />
      <ellipse cx={cx} cy={cy} rx={rx} ry={ry}
        fill="none" stroke="#c9a84c" strokeWidth="1" opacity="0.3" />

      <g transform={`translate(${cx - 20}, ${cy - 55}) scale(1.8)`}>
        <path d="M2 20h20v2H2zM5 17h14l2-10-5 4-4-6-4 6-5-4z"
          fill="#c9a84c" opacity="0.12" />
      </g>

      <text x={cx} y={cy + fontSize * 0.35} textAnchor="middle"
        fontSize={fontSize}
        fontFamily="Cinzel, serif" fontWeight="700" fill="#c9a84c" opacity="0.1"
        letterSpacing={Math.round(fontSize * 0.2)}>
        XAP XAM
      </text>

      {[
        { s: '\u2660', x: cx - 80, y: cy - 40 },
        { s: '\u2665', x: cx + 80, y: cy - 40 },
        { s: '\u2666', x: cx - 80, y: cy + 50 },
        { s: '\u2663', x: cx + 80, y: cy + 50 },
      ].map((item, i) => (
        <text key={i} x={item.x} y={item.y} textAnchor="middle"
          fontSize="22" fill="#c9a84c" opacity="0.06">{item.s}</text>
      ))}
    </svg>
  );
}

/* ==============================
   HELPERS
   ============================== */
function getInitials(name) {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
}

function hashColor(name) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 40%, 35%)`;
}

const GAME_HISTORY_RETENTION_MS = 30 * 24 * 60 * 60 * 1000;

function getGameHistoryDateTime(game) {
  const parsed = Date.parse(game?.date || '');
  return Number.isNaN(parsed) ? null : parsed;
}

function getGameHistoryTime(game) {
  if (typeof game?.savedAt === 'number') return game.savedAt;
  return getGameHistoryDateTime(game);
}

function getGameHistoryNumber(game) {
  const match = String(game?.gameNumber || '').match(/\d+/);
  return match ? parseInt(match[0], 10) : 0;
}

function pruneGameHistory(history) {
  const cutoff = Date.now() - GAME_HISTORY_RETENTION_MS;
  return (history || [])
    .map((game, index) => ({ game: { ...game, savedAt: getGameHistoryTime(game) }, index }))
    .filter(({ game }) => getGameHistoryDateTime(game) !== null)
    .filter(({ game }) => game.savedAt >= cutoff)
    .sort((a, b) => (
      getGameHistoryTime(b.game) - getGameHistoryTime(a.game)
      || getGameHistoryNumber(b.game) - getGameHistoryNumber(a.game)
      || b.index - a.index
    ))
    .map(({ game }) => game);
}

/* ==============================
   MAIN APP
   ============================== */
function App() {
  // --- State (sacred logic preserved) ---
  const [players, setPlayers] = useState([]);
  const [newPlayerName, setNewPlayerName] = useState('');
  const [scores, setScores] = useState({});
  const [totalScore, setTotalScore] = useState(0);
  const [error, setError] = useState('');
  const [scoreHistory, setScoreHistory] = useState([]);
  const [gameHistory, setGameHistory] = useState([]);
  const [removedPlayers, setRemovedPlayers] = useState([]);

  // Seat order + avatars
  const [seatOrder, setSeatOrder] = useState([]);
  const [playerAvatars, setPlayerAvatars] = useState({});
  const [customAvatars, setCustomAvatars] = useState([]);
  const [seatClaims, setSeatClaims] = useState({}); // { playerName: claimerUid }

  // Load custom avatar list from avatars.json (no rebuild needed to add new avatars)
  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/avatars/avatars.json`)
      .then(r => r.ok ? r.json() : [])
      .catch(() => [])
      .then(list => setCustomAvatars(Array.isArray(list) ? list : []));
  }, []);

  // UI modals
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [playersToRemove, setPlayersToRemove] = useState([]);
  const [isNewGameConfirm, setIsNewGameConfirm] = useState(false);
  const [isGameHistoryOpen, setIsGameHistoryOpen] = useState(false);
  const [selectedHistoryGame, setSelectedHistoryGame] = useState(null);
  const [isUndoConfirm, setIsUndoConfirm] = useState(false);
  const [avatarPickerPlayer, setAvatarPickerPlayer] = useState(null);
  const [scoreEntryPlayer, setScoreEntryPlayer] = useState(null);
  const [scorekeeperView, setScorekeeperView] = useState('table');
  const [isScoreMenuOpen, setIsScoreMenuOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenNotice, setFullscreenNotice] = useState('');
  const pwaInstall = usePwaInstall();

  // --- Game Mode (score tracker vs card game) ---
  const [currentMode, setCurrentMode] = useState('game'); // 'game' = GameShell entry | 'score' = Score Tracker

  // Scorekeeper supports both orientations; the wide table scrolls in portrait.
  useEffect(() => {
    if (currentMode !== 'score') return;
    if (window.screen.orientation && window.screen.orientation.unlock) {
      window.screen.orientation.unlock();
    }
  }, [currentMode]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!(document.fullscreenElement || document.webkitFullscreenElement));
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    if (!fullscreenNotice) return undefined;
    const timeoutId = window.setTimeout(() => setFullscreenNotice(''), 4500);
    return () => window.clearTimeout(timeoutId);
  }, [fullscreenNotice]);

  const toggleFullscreen = async () => {
    setFullscreenNotice('');
    const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement;

    try {
      if (fullscreenElement) {
        const exit = document.exitFullscreen || document.webkitExitFullscreen;
        if (exit) await exit.call(document);
        return;
      }

      const root = document.documentElement;
      if (root.requestFullscreen) {
        try {
          await root.requestFullscreen({ navigationUI: 'hide' });
        } catch (error) {
          await root.requestFullscreen();
        }
      } else if (root.webkitRequestFullscreen) {
        await root.webkitRequestFullscreen();
      } else {
        setFullscreenNotice('Open in Chrome or install Xap Xam to use fullscreen.');
        return;
      }

      if (window.screen.orientation && window.screen.orientation.unlock) {
        window.screen.orientation.unlock();
      }
    } catch (error) {
      setFullscreenNotice('Fullscreen is blocked here. Open in Chrome or install Xap Xam.');
    }
  };

  // --- Responsive ---
  const checkMobile = useCallback(() => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    return w <= 768 || (h <= 500 && w <= 1024);
  }, []);

  const [isMobile, setIsMobile] = useState(() => checkMobile());
  const [sidebarOpen, setSidebarOpen] = useState(true);
  useEffect(() => {
    const handler = () => {
      const mobile = checkMobile();
      setIsMobile(mobile);
    };
    const orientationHandler = () => setTimeout(handler, 150);
    window.addEventListener('resize', handler);
    window.addEventListener('orientationchange', orientationHandler);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('orientationchange', orientationHandler);
    };
  }, [checkMobile]);

  // --- Multiplayer ---
  const multiplayer = useMultiplayer();
  const isViewer = multiplayer.mode === 'viewer';
  const dealerSyncBlocked = multiplayer.mode === 'leader' && (
    !multiplayer.connected || multiplayer.stateSyncStatus === 'error'
  );
  const isLocked = isViewer || multiplayer.rejoining || dealerSyncBlocked;
  const notInRoom = multiplayer.mode === 'solo' && !multiplayer.rejoining;

  useEffect(() => {
    setScoreEntryPlayer(null);
  }, [isLocked, notInRoom, multiplayer.roomCode, scoreHistory.length, currentMode]);

  // --- Load from localStorage ---
  // storageLoaded gates the save effect below: without it, the save effect's
  // first run (initial empty state) overwrites storage before this load's
  // setState lands — permanent data loss under StrictMode's double effects.
  // Must be state (not a ref) so saves stay blocked until the loaded values
  // have actually rendered.
  const [storageLoaded, setStorageLoaded] = useState(false);
  useEffect(() => {
    try {
      const sp = JSON.parse(localStorage.getItem(storageKey('players'))) || [];
      const ss = JSON.parse(localStorage.getItem(storageKey('scores'))) || {};
      const sh = JSON.parse(localStorage.getItem(storageKey('scoreHistory'))) || [];
      const sr = JSON.parse(localStorage.getItem(storageKey('removedPlayers'))) || [];
      const sg = pruneGameHistory(JSON.parse(localStorage.getItem(storageKey('gameHistory'))) || []);
      const so = JSON.parse(localStorage.getItem(storageKey('seatOrder'))) || [];
      const pa = JSON.parse(localStorage.getItem(storageKey('playerAvatars'))) || {};
      const sc = JSON.parse(localStorage.getItem(storageKey('seatClaims'))) || {};
      setPlayers(sp);
      setScores(ss);
      setScoreHistory(sh);
      setRemovedPlayers(sr);
      setGameHistory(sg);
      setSeatOrder(so);
      setPlayerAvatars(pa);
      setSeatClaims(sc);
    } catch (e) { /* ignore corrupt storage */ }
    setStorageLoaded(true);
  }, []);

  // --- Save to localStorage ---
  useEffect(() => {
    if (!storageLoaded) return;
    localStorage.setItem(storageKey('players'), JSON.stringify(players));
    localStorage.setItem(storageKey('scores'), JSON.stringify(scores));
    localStorage.setItem(storageKey('scoreHistory'), JSON.stringify(scoreHistory));
    localStorage.setItem(storageKey('removedPlayers'), JSON.stringify(removedPlayers));
    localStorage.setItem(storageKey('gameHistory'), JSON.stringify(pruneGameHistory(gameHistory)));
    localStorage.setItem(storageKey('seatOrder'), JSON.stringify(seatOrder));
    localStorage.setItem(storageKey('playerAvatars'), JSON.stringify(playerAvatars));
    localStorage.setItem(storageKey('seatClaims'), JSON.stringify(seatClaims));
  }, [storageLoaded, players, scores, scoreHistory, removedPlayers, gameHistory, seatOrder, playerAvatars, seatClaims]);

  // --- Multiplayer: Dealer pushes state to Firebase ---
  const { pushGameState } = multiplayer;
  useEffect(() => {
    if (multiplayer.mode !== 'leader') return;
    pushGameState({
      players, scores, scoreHistory, removedPlayers,
      seatOrder, playerAvatars, gameHistory: pruneGameHistory(gameHistory),
    });
  }, [multiplayer.mode, pushGameState, players, scores, scoreHistory, removedPlayers, seatOrder, playerAvatars, gameHistory]);

  // --- Multiplayer: Viewer receives remote state ---
  useEffect(() => {
    if (multiplayer.mode !== 'viewer' || !multiplayer.remoteGameState) return;
    const gs = multiplayer.remoteGameState;
    setPlayers(gs.players || []);
    setScores(gs.scores || {});
    setScoreHistory(gs.scoreHistory || []);
    setRemovedPlayers(gs.removedPlayers || []);
    setSeatOrder(gs.seatOrder || []);
    // playerAvatars NOT synced here — synced via remotePlayerAvatars
    setGameHistory(pruneGameHistory(gs.gameHistory || []));
  }, [multiplayer.mode, multiplayer.remoteGameState]);

  // --- Multiplayer: Sync remote player avatars ---
  useEffect(() => {
    if (multiplayer.mode === 'solo' || !multiplayer.remotePlayerAvatars) return;
    setPlayerAvatars(multiplayer.remotePlayerAvatars);
  }, [multiplayer.mode, multiplayer.remotePlayerAvatars]);

  // --- Multiplayer: Sync remote seat claims ---
  useEffect(() => {
    if (multiplayer.mode === 'solo' || !multiplayer.remoteSeatClaims) return;
    setSeatClaims(multiplayer.remoteSeatClaims);
  }, [multiplayer.mode, multiplayer.remoteSeatClaims]);

  // --- Core logic (sacred) ---
  const addPlayer = () => {
    const name = newPlayerName.trim();
    if (!name) return;
    const activeCount = players.filter(p => !removedPlayers.includes(p)).length;
    if (activeCount >= 8 && !removedPlayers.includes(name)) {
      setError('This table supports up to 8 active players.');
      return;
    }
    setError('');
    // If the name is a removed player, restore them
    if (removedPlayers.includes(name)) {
      setRemovedPlayers(removedPlayers.filter(p => p !== name));
      setSeatOrder(prev => [...prev, name]);
      setNewPlayerName('');
      return;
    }
    // Otherwise add as new player
    if (!players.includes(name)) {
      setPlayers([...players, name]);
      setScores({ ...scores, [name]: '' });
      setSeatOrder(prev => [...prev, name]);
      setNewPlayerName('');
    }
  };

  const confirmRemovePlayers = () => {
    setRemovedPlayers([...removedPlayers, ...playersToRemove]);
    setSeatOrder(prev => prev.filter(p => !playersToRemove.includes(p)));
    setPlayersToRemove([]);
    setIsRemoveModalOpen(false);
  };

  const updateScore = (player, value) => {
    if (isLocked || notInRoom || !players.includes(player) || removedPlayers.includes(player)) return;
    if (value === '' || /^-?\d*$/.test(value)) {
      setScores(previous => ({ ...previous, [player]: value }));
    }
  };

  const calculateTotalScore = (roundScores = scores) => {
    if (isLocked || notInRoom) return 'Scoring is unavailable until the dealer is connected.';
    let sum = 0;

    players.forEach(player => {
      if (!removedPlayers.includes(player)) {
        const score = parseInt(roundScores[player] || '0', 10);
        sum += isNaN(score) ? 0 : score;
      }
    });

    const difference = sum;
    setTotalScore(difference);
    if (difference !== 0) {
      setError(`The scores are off by ${difference > 0 ? '+' : ''}${difference}.`);
      return `The scores are off by ${difference > 0 ? '+' : ''}${difference}.`;
    } else {
      setError('');
      const newRoundScores = {};
      players.forEach(player => {
        newRoundScores[player] = roundScores[player];
      });
      setScoreHistory([...scoreHistory, newRoundScores]);

      const newScores = { ...roundScores };
      players.forEach(player => {
        if (!removedPlayers.includes(player)) {
          newScores[player] = '';
        }
      });
      setScores(newScores);
      return null;
    }
  };

  const calculatePlayerSum = useCallback((player) => {
    return scoreHistory.reduce((sum, round) => sum + (parseInt(round[player], 10) || 0), 0);
  }, [scoreHistory]);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') addPlayer();
  };

  const togglePlayerSelection = (player) => {
    if (playersToRemove.includes(player)) {
      setPlayersToRemove(playersToRemove.filter(p => p !== player));
    } else {
      setPlayersToRemove([...playersToRemove, player]);
    }
  };

  // --- Scoreboard column reordering (drives seat positions too) ---
  const movePlayerLeft = (playerName) => {
    setSeatOrder(prev => {
      const arr = [...prev];
      const idx = arr.indexOf(playerName);
      if (idx < 0) return arr;
      if (idx === 0) {
        arr.splice(idx, 1);
        arr.push(playerName);
      } else {
        [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
      }
      return arr;
    });
  };

  const movePlayerRight = (playerName) => {
    setSeatOrder(prev => {
      const arr = [...prev];
      const idx = arr.indexOf(playerName);
      if (idx < 0) return arr;
      if (idx >= arr.length - 1) {
        arr.splice(idx, 1);
        arr.unshift(playerName);
      } else {
        [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
      }
      return arr;
    });
  };

  // --- Avatar assignment (blocked if seat claimed by another player) ---
  const assignAvatar = (playerName, avatarId) => {
    // Block if this seat is claimed by someone else
    const claimedBy = seatClaims[playerName];
    if (claimedBy && multiplayer.uid && claimedBy !== multiplayer.uid) return;

    setPlayerAvatars(prev => ({ ...prev, [playerName]: avatarId }));
    setAvatarPickerPlayer(null);
    if (multiplayer.mode !== 'solo') {
      multiplayer.pushAvatarChange(playerName, avatarId);
    }
  };

  const clearAvatar = (playerName) => {
    // Block if this seat is claimed by someone else
    const claimedBy = seatClaims[playerName];
    if (claimedBy && multiplayer.uid && claimedBy !== multiplayer.uid) return;

    setPlayerAvatars(prev => {
      const next = { ...prev };
      delete next[playerName];
      return next;
    });
    setAvatarPickerPlayer(null);
    if (multiplayer.mode !== 'solo') {
      multiplayer.pushAvatarChange(playerName, null);
    }
  };

  const isCustomAvatar = (playerName) => {
    const id = playerAvatars[playerName];
    return id && id.startsWith('custom-');
  };

  const renderAvatarContent = (playerName, size) => {
    const avatarId = playerAvatars[playerName];
    if (avatarId) {
      const preset = PRESET_AVATARS.find(a => a.id === avatarId);
      if (preset) return preset.render(size);
      if (avatarId.startsWith('custom-')) {
        const filename = avatarId.replace('custom-', '');
        return <img src={`${process.env.PUBLIC_URL}/avatars/${filename}`} alt={playerName} />;
      }
    }
    return (
      <span className="seat-avatar-initials" style={{ fontSize: size * 0.5 }}>
        {getInitials(playerName)}
      </span>
    );
  };

  // --- Derived data ---
  const activePlayers = players.filter(p => !removedPlayers.includes(p));
  const allPlayers = players.concat(removedPlayers.filter(rp => !players.includes(rp)));

  // Ordered lists based on seatOrder
  const orderedActivePlayers = (() => {
    const ordered = seatOrder.filter(p => activePlayers.includes(p));
    const missing = activePlayers.filter(p => !ordered.includes(p));
    return [...ordered, ...missing];
  })();

  const orderedAllPlayers = (() => {
    const ordered = seatOrder.filter(p => allPlayers.includes(p));
    const missing = allPlayers.filter(p => !ordered.includes(p));
    return [...ordered, ...missing];
  })();

  const standings = activePlayers
    .map(p => ({ name: p, total: calculatePlayerSum(p) }))
    .sort((a, b) => b.total - a.total);
  const visibleGameHistory = pruneGameHistory(gameHistory);
  const selectedVisibleGame = selectedHistoryGame !== null ? visibleGameHistory[selectedHistoryGame] : null;

  // Crown logic: player(s) with highest score get a gold crown
  const highestScore = standings.length > 0 ? standings[0].total : 0;
  const leadingPlayers = scoreHistory.length > 0 && highestScore > 0
    ? standings.filter(s => s.total === highestScore).map(s => s.name)
    : [];

  const returnToMainMenu = () => {
    setIsScoreMenuOpen(false);
    setScorekeeperView('table');
    setCurrentMode('game');
  };

  // --- New Game ---
  const startNewGame = () => {
    if (scoreHistory.length > 0 || players.length > 0) {
      const today = new Date().toLocaleDateString();
      const newGame = {
        gameNumber: `Game ${gameHistory.length + 1}`,
        date: today,
        savedAt: Date.now(),
        rounds: scoreHistory,
        players: allPlayers,
      };
      setGameHistory(prev => pruneGameHistory([...prev, newGame]));
    }
    setPlayers([]);
    setScores({});
    setScoreHistory([]);
    setRemovedPlayers([]);
    setSeatOrder([]);
    setSeatClaims({});
    setTotalScore(0);
    setError('');
    setIsNewGameConfirm(false);
  };

  // --- Restore historical game ---
  const restoreGame = async (game) => {
    if (!game) return;
    const restoredPlayers = game.players || [];
    const restoredScores = {};
    restoredPlayers.forEach(p => { restoredScores[p] = ''; });
    const restoredState = {
      players: restoredPlayers,
      scores: restoredScores,
      scoreHistory: game.rounds || [],
      removedPlayers: [],
      seatOrder: restoredPlayers,
      playerAvatars,
      gameHistory,
    };

    let nextHistory = gameHistory;
    if (scoreHistory.length > 0 || players.length > 0) {
      const today = new Date().toLocaleDateString();
      const currentGame = {
        gameNumber: `Game ${gameHistory.length + 1}`,
        date: today,
        savedAt: Date.now(),
        rounds: scoreHistory,
        players: allPlayers,
      };
      nextHistory = pruneGameHistory([...gameHistory, currentGame]);
      restoredState.gameHistory = nextHistory;
    }

    if (multiplayer.mode === 'solo') {
      const createdCode = await multiplayer.createRoom(restoredState);
      if (!createdCode) return;
    }

    setGameHistory(nextHistory);
    setPlayers(restoredPlayers);
    setScores(restoredScores);
    setScoreHistory(game.rounds || []);
    setRemovedPlayers([]);
    setSeatOrder(restoredPlayers);
    setTotalScore(0);
    setError('');
    setSelectedHistoryGame(null);
    setIsUndoConfirm(false);
    setIsGameHistoryOpen(false);
  };

  // --- Seat positioning ---
  const seatPositions = (count) => {
    if (count === 0) return [];

    const cx = TABLE_W / 2;
    const cy = TABLE_H / 2;
    const { seatRx, seatRy } = getSeatEllipseRadii(isMobile, count);
    const seatIndexes = SEAT_INDEX_MAP[count] || SEAT_INDEX_MAP[8].slice(0, count);

    return seatIndexes.map((seatIndex) => {
      const angle = SEAT_ANCHOR_ANGLES[seatIndex];
      const x = cx + seatRx * Math.cos(angle);
      const y = cy + seatRy * Math.sin(angle);

      return {
        seatIndex,
        left: `${(x / TABLE_W) * 100}%`,
        top: `${(y / TABLE_H) * 100}%`,
      };
    });
  };

  const seats = seatPositions(orderedActivePlayers.length);

  // Score color helper
  const scoreColor = (val) => {
    const n = parseInt(val, 10);
    if (isNaN(n) || n === 0) return '#8a7d6b';
    return n > 0 ? '#4ade80' : '#f87171';
  };

  const scoreClass = (val) => {
    const n = parseInt(val, 10);
    if (isNaN(n) || n === 0) return 'score-zero';
    return n > 0 ? 'score-positive' : 'score-negative';
  };

  // ======================
  // RENDER
  // ======================

  // --- GameShell (default entry point) ---
  if (currentMode === 'game') {
    return (
      <GameShell
        avatars={customAvatars}
        onExitToMenu={() => setCurrentMode('game')}
        onScoreTracker={() => setCurrentMode('score')}
        pwaInstall={pwaInstall}
      />
    );
  }

  // --- Score Tracker Mode (existing app) ---
  return (
    <div className="app-shell scorekeeper-shell">

      <header className="scorekeeper-topbar">
        <button className="score-icon-btn" onClick={returnToMainMenu} aria-label="Back to main menu">&#8592;</button>
        <button
          className="score-room-chip"
          onClick={() => multiplayer.roomCode && navigator.clipboard?.writeText(multiplayer.roomCode)}
          disabled={!multiplayer.roomCode}
          title={multiplayer.roomCode ? 'Copy room code' : 'Create or join a room'}
        >
          <span className="connection-dot" style={{ background: multiplayer.connected ? '#4ade80' : '#fbbf24' }} />
          <span>{multiplayer.roomCode || 'No Room'}</span>
          {multiplayer.roomCode && <small>{isViewer ? 'Viewing' : 'Dealer'}</small>}
        </button>
        <div className="scorekeeper-brand">
          {scorekeeperView === 'table' && activePlayers.length > 0 && !isLocked && !notInRoom && (
            <button className="score-submit-btn" onClick={() => calculateTotalScore()}>Submit</button>
          )}
          <span>Xap Xam</span>
          <strong>R{scoreHistory.length + 1}</strong>
        </div>
        <div className="score-topbar-actions">
          {!notInRoom && (
            <button
              className={`score-nav-btn${scorekeeperView === 'scoreboard' ? ' active' : ''}`}
              onClick={() => setScorekeeperView(scorekeeperView === 'scoreboard' ? 'table' : 'scoreboard')}
            >
              {scorekeeperView === 'scoreboard' ? 'Back to Scoring' : 'Scoreboard'}
            </button>
          )}
          {!pwaInstall.isInstalled && (
            <button
              className={`score-icon-btn score-fullscreen-btn${isFullscreen ? ' active' : ''}`}
              onClick={toggleFullscreen}
              aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
              title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M9 3H3v6M15 3h6v6M9 21H3v-6M15 21h6v-6" />
              </svg>
            </button>
          )}
          <button
            className="score-icon-btn score-menu-btn"
            onClick={() => { setIsScoreMenuOpen(true); setSidebarOpen(true); }}
            aria-label="Open scorekeeper menu"
          >
            <span /><span /><span />
          </button>
        </div>
      </header>

      {fullscreenNotice && <div className="fullscreen-toast" role="status">{fullscreenNotice}</div>}

      {/* ===== MOBILE SIDEBAR TOGGLE ===== */}
      {isMobile && (
        <button
          className="sidebar-toggle"
          onClick={() => setSidebarOpen(prev => !prev)}
          title={sidebarOpen ? 'Hide menu' : 'Show menu'}
        >
          {sidebarOpen ? '\u25B2 Hide Menu' : '\u25BC Menu'}
        </button>
      )}

      {/* ===== LEFT PANEL ===== */}
      {isScoreMenuOpen && <button className="score-menu-scrim" onClick={() => setIsScoreMenuOpen(false)} aria-label="Close menu" />}
      <div className={`left-panel score-menu-drawer${isScoreMenuOpen ? ' score-menu-drawer-open' : ''}${isMobile && !sidebarOpen ? ' left-panel-hidden' : ''}`}>
        <div className="score-menu-heading">
          <span>Game Menu</span>
          <button className="score-icon-btn" onClick={() => setIsScoreMenuOpen(false)} aria-label="Close menu">&times;</button>
        </div>
        <button className="btn btn-muted btn-sm" onClick={() => setCurrentMode('game')} style={{ marginBottom: 8 }}>
          ← Back
        </button>
        <div className="panel-title">Xap Xam</div>

        <div className="score-install-panel" data-installed={pwaInstall.isInstalled ? 'true' : 'false'}>
          {pwaInstall.isInstalled ? (
            <div className="score-install-state">
              <span className="connection-dot" />
              Installed app mode
            </div>
          ) : (
            <button className="btn btn-gold btn-sm" onClick={pwaInstall.install}>
              Install Xap Xam
            </button>
          )}
          {pwaInstall.message && (
            <p className="score-install-message" role="status">{pwaInstall.message}</p>
          )}
        </div>

        {/* Multiplayer Room Panel */}
        <RoomPanel
          multiplayer={multiplayer}
          onCreateRoom={() => {
            if (scoreHistory.length > 0 || players.length > 0) {
              const today = new Date().toLocaleDateString();
                const newGame = {
                  gameNumber: `Game ${gameHistory.length + 1}`,
                  date: today,
                  savedAt: Date.now(),
                  rounds: scoreHistory,
                  players: allPlayers,
                };
                setGameHistory(prev => {
                  const updated = pruneGameHistory([...prev, newGame]);
                  multiplayer.createRoom({
                    players: [], scores: {}, scoreHistory: [], removedPlayers: [],
                    seatOrder: [], playerAvatars: {}, gameHistory: updated,
                });
                return updated;
              });
            } else {
              multiplayer.createRoom({
                players: [], scores: {}, scoreHistory: [], removedPlayers: [],
                seatOrder: [], playerAvatars: {}, gameHistory,
              });
            }
            setPlayers([]);
            setScores({});
            setScoreHistory([]);
            setRemovedPlayers([]);
            setSeatOrder([]);
            setSeatClaims({});
            setTotalScore(0);
            setError('');
          }}
        />
        <hr className="panel-separator" />

        {/* Live Standings */}
        {scoreHistory.length > 0 && !notInRoom && (
          <div>
            <div className="section-label">Standings</div>
            {standings.map((s, i) => (
              <div key={s.name} className="standings-row">
                <span className="standings-rank">{i + 1}.</span>
                <span className="standings-name">{s.name}</span>
                <span className={scoreClass(s.total)} style={{ fontWeight: 700 }}>
                  {s.total > 0 ? '+' : ''}{s.total}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Game History */}
        <button
          className="btn btn-muted btn-full"
          onClick={() => setIsGameHistoryOpen(true)}
        >
          Game History
        </button>

        {/* New Game */}
        {!isViewer && !notInRoom && (
          <button
            className="btn btn-red btn-full"
            onClick={() => setIsNewGameConfirm(true)}
          >
            New Game
          </button>
        )}

        {/* Separator */}
        <hr className="panel-separator" />

        {/* Add Player (moved to bottom) */}
        {!isViewer && !notInRoom && (
          <div>
            <div className="section-label">Add Player</div>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                className="poker-input"
                type="text"
                value={newPlayerName}
                onChange={e => setNewPlayerName(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Player name"
                style={{ flex: 1 }}
                disabled={activePlayers.length >= 8}
              />
              <button className="btn btn-gold btn-sm" onClick={addPlayer} disabled={activePlayers.length >= 8}>+</button>
            </div>
          </div>
        )}

        {/* Active Players + Remove */}
        {!notInRoom && activePlayers.length > 0 && (
          <div>
            <div className="section-label">Manage Players</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {orderedActivePlayers.map(p => (
                <button key={p} className="player-chip player-manage-button"
                  onClick={() => { setAvatarPickerPlayer(p); setIsScoreMenuOpen(false); }}
                  aria-label={`Manage ${p}`}>{p}</button>
              ))}
            </div>
            {!isViewer && (
              <div className="score-menu-button-grid" style={{ marginTop: 8 }}>
                <button
                  className="btn btn-muted btn-sm"
                  onClick={() => { setIsRemoveModalOpen(true); setIsScoreMenuOpen(false); }}
                >
                  Remove Players
                </button>
                <button
                  className="btn btn-muted btn-sm"
                  onClick={() => { setScorekeeperView('scoreboard'); setIsScoreMenuOpen(false); }}
                >
                  Reorder Seats
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ===== RIGHT PANEL ===== */}
      <div className={`right-panel score-view-${scorekeeperView}`}>

        {multiplayer.rejoining && (
          <div className="viewer-banner">
            Reconnecting to room&hellip;
          </div>
        )}

        {isViewer && (
          <div className="viewer-banner">
            Viewing Live &mdash; Scores Updated by Dealer
          </div>
        )}

        {/* Rejoin banner after leaving a room */}
        {multiplayer.mode === 'solo' && multiplayer.lastLeftRoomCode && (
          <div className="rejoin-banner">
            <span>You left room</span>
            <span className="rejoin-code">{multiplayer.lastLeftRoomCode}</span>
            <button className="btn btn-gold btn-sm" onClick={() => multiplayer.joinRoom(multiplayer.lastLeftRoomCode)}>
              Rejoin
            </button>
            <button className="btn btn-muted btn-sm" onClick={() => multiplayer.clearLastLeftRoom()}>
              Dismiss
            </button>
          </div>
        )}

        {/* Poker Table */}
        <div className="score-table-scroll-hint">Swipe left or right to see all players</div>
        <div
          className={`table-area scorekeeper-table${orderedActivePlayers.length >= 7 ? ' table-area--crowded' : ''}`}
          data-player-count={orderedActivePlayers.length}
        >
          <PokerTableSVG width={TABLE_W} height={TABLE_H} />

          {/* No players message */}
          {!notInRoom && orderedActivePlayers.length === 0 && (
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'Cinzel, serif', fontSize: 16, color: '#8a7d6b',
              letterSpacing: 2, zIndex: 2,
            }}>
              Add players to begin
            </div>
          )}

          {/* Not in room prompt */}
          {notInRoom && (
            <div className="table-error-overlay">
              <div className="table-error-box table-info-box">
                Create or Join a Room
              </div>
            </div>
          )}

          {/* Error overlay on table */}
          {error && (
            <div className="table-error-overlay">
              <div className={`table-error-box ${totalScore !== 0 ? '' : 'warn'}`}>
                {error}
              </div>
            </div>
          )}

          {/* Seats */}
          {!notInRoom && orderedActivePlayers.map((player, i) => {
            const hasCustom = isCustomAvatar(player);
            const sum = calculatePlayerSum(player);
            const isClaimed = !!seatClaims[player];

            return (
              <div
                key={player}
                className="seat"
                data-seat-anchor={seats[i]?.seatIndex}
                style={{ left: seats[i]?.left, top: seats[i]?.top }}
              >
                {leadingPlayers.includes(player) && (
                  <div className="seat-crown">
                    <svg viewBox="0 0 24 24" width={28} height={28} fill="#c9a84c">
                      <path d="M2 20h20v2H2zM5 17h14l2-10-5 4-4-6-4 6-5-4z"/>
                    </svg>
                  </div>
                )}
                <button
                  className={`seat-avatar${isClaimed ? ' seat-avatar-leader' : ''}`}
                  style={{ background: !hasCustom ? (playerAvatars[player] ? '#1a1d24' : hashColor(player)) : undefined }}
                  onClick={() => setScoreEntryPlayer(player)}
                  disabled={isLocked}
                  aria-label={`Enter score for ${player}`}
                  title={`Enter score for ${player}`}
                >
                  {renderAvatarContent(player, hasCustom ? 100 : 50)}
                  <span
                    className="seat-name-overlay"
                    style={isClaimed ? { fontWeight: 700 } : undefined}
                  >
                    {player}
                  </span>
                </button>
                <span className="seat-total" style={{ color: scoreColor(sum) }}>
                  {sum !== 0 ? (sum > 0 ? '+' : '') + sum : '0'}
                </span>
                <div className="seat-controls">
                  <button
                    className="seat-round-badge"
                    onClick={() => setScoreEntryPlayer(player)}
                    disabled={isLocked}
                    aria-label={`${player} this round: ${displayRoundScore(scores[player])}`}
                  >
                    <span>This round</span> <strong>{displayRoundScore(scores[player])}</strong>
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Score History Table — totals on top, column reorder arrows */}
        {scoreHistory.length > 0 && !notInRoom && (
          <div className="history-wrap scoreboard-screen">
            <div className="scoreboard-heading">
              <div>
                <span>Current Game</span>
                <strong>Scoreboard</strong>
              </div>
              <small>{activePlayers.length} players &bull; {scoreHistory.length} rounds</small>
            </div>
            <table className="history-table">
              <thead>
                <tr>
                  <th>Round</th>
                  {orderedAllPlayers.map(p => (
                    <th key={p}>
                      {!isViewer && <button className="th-arrow-btn" onClick={() => movePlayerLeft(p)} title="Move left">&#9664;</button>}
                      {' '}{p}{' '}
                      {!isViewer && <button className="th-arrow-btn" onClick={() => movePlayerRight(p)} title="Move right">&#9654;</button>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* TOTAL row on top */}
                <tr className="score-total-row">
                  <td style={{ fontFamily: 'Cinzel, serif', color: '#c9a84c' }}>Total</td>
                  {orderedAllPlayers.map(p => {
                    const sum = calculatePlayerSum(p);
                    return (
                      <td key={p} style={{ color: scoreColor(sum) }}>
                        {sum > 0 ? '+' : ''}{sum}
                      </td>
                    );
                  })}
                </tr>

                {/* Rounds in newest-first order */}
                {scoreHistory.slice().reverse().map((round, ri) => {
                  const roundNum = scoreHistory.length - ri;
                  return (
                    <tr key={ri}>
                      <td style={{ color: '#8a7d6b', fontFamily: 'Cinzel, serif', fontSize: 11 }}>
                        R{roundNum}
                      </td>
                      {orderedAllPlayers.map(p => {
                        const val = round[p];
                        return (
                          <td key={p} style={{ color: scoreColor(val) }}>
                            {val !== undefined && val !== '' ? val : '\u2014'}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {scorekeeperView === 'scoreboard' && !notInRoom && scoreHistory.length === 0 && (
          <div className="scoreboard-empty">
            <strong>Scoreboard</strong>
            <span>No rounds have been submitted yet.</span>
          </div>
        )}

        {scorekeeperView === 'scoreboard' && !notInRoom && (
          <div className="scoreboard-actions">
            <button className="btn btn-gold" onClick={() => setScorekeeperView('table')}>Back to Scoring</button>
            <button className="btn btn-muted" onClick={() => setIsGameHistoryOpen(true)}>Saved Games</button>
            {!isViewer && activePlayers.length > 0 && (
              <button className="btn btn-muted" onClick={() => setIsRemoveModalOpen(true)}>Remove Players</button>
            )}
          </div>
        )}
      </div>

      {/* ===== MODALS ===== */}

      {/* Remove Players Modal */}
      {isRemoveModalOpen && (
        <div className="modal-overlay" onClick={() => setIsRemoveModalOpen(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-title">Remove Players</div>
            <div className="modal-text">
              Removed players stay in the score history but are removed from score entry.
            </div>
            {activePlayers.map(p => (
              <label key={p} className="modal-check-item">
                <input
                  type="checkbox"
                  checked={playersToRemove.includes(p)}
                  onChange={() => togglePlayerSelection(p)}
                />
                {p}
              </label>
            ))}
            <div className="modal-actions" style={{ marginTop: 16 }}>
              <button className="btn btn-red" onClick={confirmRemovePlayers}>
                Remove Selected
              </button>
              <button className="btn btn-muted" onClick={() => { setIsRemoveModalOpen(false); setPlayersToRemove([]); }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Game Confirmation Modal */}
      {isNewGameConfirm && (
        <div className="modal-overlay" onClick={() => setIsNewGameConfirm(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-title">New Game</div>
            <div className="modal-text">
              All players and score history will be wiped. This cannot be undone.
              {scoreHistory.length > 0 && ' The current game will be saved to history.'}
            </div>
            <div className="modal-actions">
              <button className="btn btn-red" onClick={startNewGame}>
                Start New Game
              </button>
              <button className="btn btn-muted" onClick={() => setIsNewGameConfirm(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {scoreEntryPlayer && !isLocked && !notInRoom && orderedActivePlayers.includes(scoreEntryPlayer) && (
        <ScoreEntry initialPlayer={scoreEntryPlayer} players={orderedActivePlayers}
          scores={scores} round={scoreHistory.length + 1} totalFor={calculatePlayerSum}
          renderAvatar={renderAvatarContent} onSave={updateScore} onSubmit={calculateTotalScore} onClose={() => setScoreEntryPlayer(null)} />
      )}

      {/* Avatar Picker Modal — reached through Manage Players, not score entry */}
      {avatarPickerPlayer && (() => {
        const claimedByUid = seatClaims[avatarPickerPlayer];
        const myUid = multiplayer.uid;
        const isMyClaimedSeat = claimedByUid && claimedByUid === myUid;
        const isOtherClaimedSeat = claimedByUid && claimedByUid !== myUid;
        const inMultiplayer = multiplayer.mode !== 'solo';

        return (
          <div className="modal-overlay" onClick={() => setAvatarPickerPlayer(null)}>
            <div className="modal-box avatar-picker-modal" role="dialog" aria-label={`Avatar for ${avatarPickerPlayer}`} onClick={e => e.stopPropagation()}>
              <div className="avatar-picker-header">
              <div className="modal-title">Avatar for {avatarPickerPlayer}</div>

              {/* Action buttons at top */}
              <div className="modal-actions avatar-picker-actions">
                {/* Seat claim button — available to everyone in multiplayer */}
                {inMultiplayer && !isOtherClaimedSeat && (
                  <button
                    className={`btn btn-sm ${isMyClaimedSeat ? 'btn-muted' : 'btn-gold'}`}
                    onClick={() => {
                      if (isMyClaimedSeat) {
                        multiplayer.unclaimSeat(avatarPickerPlayer);
                        setSeatClaims(prev => {
                          const next = { ...prev };
                          delete next[avatarPickerPlayer];
                          return next;
                        });
                      } else {
                        multiplayer.claimSeat(avatarPickerPlayer, myUid);
                        setSeatClaims(prev => {
                          const next = {};
                          for (const [name, uid] of Object.entries(prev)) {
                            if (uid !== myUid) next[name] = uid;
                          }
                          next[avatarPickerPlayer] = myUid;
                          return next;
                        });
                      }
                      setAvatarPickerPlayer(null);
                    }}
                  >
                    {isMyClaimedSeat ? 'Unclaim Seat' : 'Claim as My Seat'}
                  </button>
                )}
                {!(inMultiplayer && isOtherClaimedSeat) && (
                  <button className="btn btn-muted btn-sm" onClick={() => clearAvatar(avatarPickerPlayer)}>
                    Clear Avatar
                  </button>
                )}
                {!isLocked && (
                  <>
                    <button className="btn btn-muted btn-sm" onClick={() => movePlayerLeft(avatarPickerPlayer)}>
                      Move Left
                    </button>
                    <button className="btn btn-muted btn-sm" onClick={() => movePlayerRight(avatarPickerPlayer)}>
                      Move Right
                    </button>
                  </>
                )}
                <button className="btn btn-muted btn-sm" onClick={() => setAvatarPickerPlayer(null)}>
                  Close
                </button>
              </div>

              </div>
              {/* If seat is claimed by another player in multiplayer, show locked message */}
              {inMultiplayer && isOtherClaimedSeat ? (
                <div style={{
                  textAlign: 'center', color: '#fbbf24', fontSize: 13,
                  padding: '20px 10px', lineHeight: 1.6,
                }}>
                  This seat is claimed by another player.
                  <br />
                  Only the player who claimed it can change the avatar.
                </div>
              ) : (
                <>
                  {/* Avatars from /avatars/avatars.json */}
                  {customAvatars.length > 0 ? (
                    <>
                      <div className="section-label" style={{ marginTop: 4 }}>Avatars</div>
                      <div className="avatar-grid">
                        {customAvatars.map(filename => {
                          const id = `custom-${filename}`;
                          return (
                            <div
                              key={id}
                              className={`avatar-option ${playerAvatars[avatarPickerPlayer] === id ? 'selected' : ''}`}
                              onClick={() => assignAvatar(avatarPickerPlayer, id)}
                              title={filename.replace(/\.[^.]+$/, '').replace(/-/g, ' ')}
                            >
                              <img
                                src={`${process.env.PUBLIC_URL}/avatars/${filename}`}
                                alt={filename}
                                style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover' }}
                              />
                            </div>
                          );
                        })}
                      </div>
                      <div className="section-label" style={{ marginTop: 12 }}>Icons</div>
                      <div className="avatar-grid">
                        {PRESET_AVATARS.map(av => (
                          <div
                            key={av.id}
                            className={`avatar-option ${playerAvatars[avatarPickerPlayer] === av.id ? 'selected' : ''}`}
                            onClick={() => assignAvatar(avatarPickerPlayer, av.id)}
                            title={av.label}
                          >
                            {av.render(30)}
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="section-label" style={{ marginTop: 4 }}>Icons</div>
                      <div className="avatar-grid">
                        {PRESET_AVATARS.map(av => (
                          <div
                            key={av.id}
                            className={`avatar-option ${playerAvatars[avatarPickerPlayer] === av.id ? 'selected' : ''}`}
                            onClick={() => assignAvatar(avatarPickerPlayer, av.id)}
                            title={av.label}
                          >
                            {av.render(30)}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })()}

      {/* Game History Modal */}
      {isGameHistoryOpen && (
        <div className="modal-overlay" onClick={() => { setIsGameHistoryOpen(false); setSelectedHistoryGame(null); }}>
          <div className="modal-box" style={{ minWidth: 380, maxWidth: 600 }} onClick={e => e.stopPropagation()}>
            <div className="modal-title">Game History</div>

            {visibleGameHistory.length === 0 ? (
              <div className="modal-text">No previous games saved.</div>
            ) : (
              <>
                {visibleGameHistory.map((game, gi) => (
                  <div
                    key={`${game.savedAt || gi}-${game.gameNumber || gi}`}
                    className={`game-history-card ${selectedHistoryGame === gi ? 'selected' : ''}`}
                    onClick={() => setSelectedHistoryGame(selectedHistoryGame === gi ? null : gi)}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontFamily: 'Cinzel, serif', color: '#c9a84c', fontSize: 14 }}>
                        {game.gameNumber || `Game ${visibleGameHistory.length - gi}`}
                      </span>
                      <span style={{ color: '#8a7d6b', fontSize: 12 }}>
                        {game.date || ''}
                      </span>
                    </div>
                    <div style={{ color: '#8a7d6b', fontSize: 12, marginTop: 4 }}>
                      {(game.players || []).join(', ')} &mdash; {(game.rounds || []).length} rounds
                    </div>

                    {selectedHistoryGame === gi && (
                      <div style={{ marginTop: 12, overflowX: 'auto' }}>
                        <table className="history-table" style={{ fontSize: 11 }}>
                          <thead>
                            <tr>
                              <th>Rd</th>
                              {(game.players || []).map(p => <th key={p}>{p}</th>)}
                            </tr>
                          </thead>
                          <tbody>
                            {(game.rounds || []).map((round, ri) => (
                              <tr key={ri}>
                                <td style={{ color: '#8a7d6b' }}>{ri + 1}</td>
                                {(game.players || []).map(p => (
                                  <td key={p} style={{ color: scoreColor(round[p]) }}>
                                    {round[p] !== undefined && round[p] !== '' ? round[p] : '\u2014'}
                                  </td>
                                ))}
                              </tr>
                            ))}
                            <tr className="score-total-row">
                              <td style={{ color: '#c9a84c' }}>T</td>
                              {(game.players || []).map(p => {
                                const s = (game.rounds || []).reduce(
                                  (acc, r) => acc + (parseInt(r[p], 10) || 0), 0
                                );
                                return (
                                  <td key={p} style={{ color: scoreColor(s) }}>
                                    {s > 0 ? '+' : ''}{s}
                                  </td>
                                );
                              })}
                            </tr>
                          </tbody>
                        </table>

                        <button
                          className="btn btn-gold btn-sm"
                          style={{ marginTop: 10, width: '100%' }}
                          onClick={(e) => { e.stopPropagation(); setIsUndoConfirm(true); }}
                        >
                          Restore This Game
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}

            <div className="modal-actions" style={{ marginTop: 16 }}>
              <button className="btn btn-muted" onClick={() => { setIsGameHistoryOpen(false); setSelectedHistoryGame(null); }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Undo / Restore Confirmation */}
      {isUndoConfirm && selectedVisibleGame && (
        <div className="modal-overlay" onClick={() => setIsUndoConfirm(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-title">Restore Game</div>
            <div className="modal-text">
              This will replace the current scoreboard with {selectedVisibleGame.gameNumber || 'the selected game'}.
              {(scoreHistory.length > 0 || players.length > 0) && ' Your current game will be saved to history first.'}
            </div>
            <div className="modal-actions">
              <button className="btn btn-gold" onClick={() => restoreGame(selectedVisibleGame)}>
                Restore
              </button>
              <button className="btn btn-muted" onClick={() => setIsUndoConfirm(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
